import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ShareRecord } from '@trove/contracts';
import type { RepositoryFile } from '@trove/core';
import { isHiddenMetaPath, visibleFiles as userVisibleFiles } from '@trove/core';
import { Button, Dialog, TextField } from '@trove/ui';
import { CaretRight, DownloadSimple, FolderLock, Users } from '@phosphor-icons/react';
import { api } from '../api';
import { EmptyState } from '../components/EmptyState';
import { FileContentPreview } from '../components/FileContentPreview';
import { FileDrawer } from '../components/FileDrawer';
import { FileGlyph, FileSheetGlyph } from '../components/FileGlyph';
import { RelativeTime } from '../components/RelativeTime';
import { downloadRepositoryFile } from '../fileKinds';
import { entriesAt, type FileTreeEntry } from '../fileTree';
import { formatSize } from '../formatSize';
import { useSession } from '../session';
import { MOBILE_QUERY, useMediaQuery } from '../useMediaQuery';
import { useWorkspace } from '../workspace';
import shared from '../styles/shared.module.css';
import styles from './shared.module.css';

interface SharedSearch {
  folder?: string;
  file?: string;
}

type Entry = FileTreeEntry;

export const Route = createFileRoute('/shared')({
  validateSearch: (search: Record<string, unknown>): SharedSearch => ({
    folder: typeof search.folder === 'string' ? search.folder : undefined,
    file: typeof search.file === 'string' ? search.file : undefined,
  }),
  component: SharedPage,
});

const statusLabel: Record<ShareRecord['status'], string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  revoked: 'Revoked',
  expired: 'Expired',
};

function SharedPage() {
  const { user } = useSession();
  const { workspace, acceptIncomingShare, syncing } = useWorkspace();
  const queryClient = useQueryClient();
  const [codeShare, setCodeShare] = useState<ShareRecord | null>(null);
  const [accessCode, setAccessCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const incoming = useQuery({
    queryKey: ['shares', 'incoming', user?.id],
    queryFn: () => api<{ shares: ShareRecord[] }>('/v1/shares?direction=incoming'),
    enabled: Boolean(user) && workspace?.mode !== 'shared',
  });
  const accept = async (share: ShareRecord, code?: string) => {
    await acceptIncomingShare(share, code);
    await queryClient.invalidateQueries({ queryKey: ['shares'] });
  };
  const openShare = (share: ShareRecord) => {
    if (share.status === 'pending' && share.requiresAccessCode) {
      setAccessCode('');
      setCodeError('');
      setCodeShare(share);
      return;
    }
    void accept(share);
  };
  const confirmCode = async () => {
    if (!codeShare) return;
    const normalized = accessCode.trim().toUpperCase();
    if (!/^[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(normalized)) {
      setCodeError('Enter the magic code as XXXX-XXXX');
      return;
    }
    try {
      await accept(codeShare, normalized);
      setCodeShare(null);
      setAccessCode('');
      setCodeError('');
    } catch (error) {
      setCodeError(error instanceof Error ? error.message : 'Magic code is incorrect');
    }
  };
  const incomingShares = incoming.data?.shares ?? [];

  if (workspace?.mode === 'shared') {
    return <SharedCollection />;
  }

  return (
    <div className={styles.sharesPage} data-full-bleed>
      {!incoming.isLoading && !incomingShares.length ? (
        <EmptyState
          icon={<Users size={20} />}
          title="Nothing shared with you yet"
          description={
            <>
              Folders other people share with <strong>{user?.email}</strong> will appear here.
            </>
          }
        />
      ) : (
        <table className={styles.sharesTable}>
          <thead>
            <tr>
              <th>Folder</th>
              <th>From</th>
              <th>Status</th>
              <th>Shared</th>
              <th className={styles.sharesActionsHead} />
            </tr>
          </thead>
          <tbody>
            {incomingShares.map((share) => (
              <tr key={share.id} className={styles.sharesRow}>
                <td>
                  <span className={styles.shareFolder}>
                    <span className={styles.shareFolderIcon}>
                      <FolderLock size={14} />
                    </span>
                    <span className={styles.shareFolderName}>
                      {share.collectionLabel || 'Shared folder'}
                    </span>
                  </span>
                </td>
                <td className={styles.shareFrom}>{share.ownerEmail ?? '—'}</td>
                <td>
                  <span
                    className={`${shared.badge} ${
                      share.status === 'accepted'
                        ? shared.badgeGreen
                        : share.status === 'pending'
                          ? shared.badgeYellow
                          : ''
                    }`}
                  >
                    {statusLabel[share.status]}
                  </span>
                </td>
                <td className={styles.shareMeta}>
                  <RelativeTime value={share.createdAt} />
                </td>
                <td className={styles.sharesActions}>
                  {share.status !== 'revoked' && share.status !== 'expired' && (
                    <Button
                      size="small"
                      variant="primary"
                      disabled={syncing}
                      onClick={() => openShare(share)}
                    >
                      {share.status === 'accepted' ? 'Open' : 'Accept'}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Dialog
        open={Boolean(codeShare)}
        onOpenChange={(open) => {
          if (!open) {
            setCodeShare(null);
            setAccessCode('');
            setCodeError('');
          }
        }}
        title="Enter magic code"
        description="This is the share code from the sender — not a device pairing code."
        actions={
          <>
            <Button onClick={() => setCodeShare(null)}>Cancel</Button>
            <Button variant="primary" disabled={syncing} onClick={() => void confirmCode()}>
              Accept
            </Button>
          </>
        }
      >
        <TextField
          label="Magic code"
          value={accessCode}
          onValueChange={setAccessCode}
          placeholder="XXXX-XXXX"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          error={codeError}
        />
      </Dialog>
    </div>
  );
}

function SharedCollection() {
  const { workspace } = useWorkspace();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/shared' });
  const isMobile = useMediaQuery(MOBILE_QUERY);
  if (!workspace) return null;

  const label = workspace.sharedLabel ?? 'Shared folder';
  const entries = entriesAt(workspace.snapshot.files, search.folder);
  const selected = workspace.snapshot.files.find(
    (file) => file.path === search.file && !isHiddenMetaPath(file.path),
  );
  const listedFiles = userVisibleFiles(
    search.folder
      ? workspace.snapshot.files.filter((file) => file.path.startsWith(`${search.folder}/`))
      : workspace.snapshot.files,
  );
  const totalSize = listedFiles.reduce((sum, file) => sum + file.size, 0);
  const breadcrumbs = search.folder?.split('/') ?? [];
  const closeFile = () => void navigate({ search: { folder: search.folder } });

  const openEntry = (entry: Entry) => {
    if (entry.type === 'folder') void navigate({ search: { folder: entry.path } });
    else void navigate({ search: { folder: search.folder, file: entry.path } });
  };

  return (
    <div className={styles.sharedCollectionPage}>
      <div className={styles.sharedCollectionHeader}>
        <h1>{search.folder?.split('/').at(-1) ?? label}</h1>
        <p>
          {listedFiles.length} {listedFiles.length === 1 ? 'file' : 'files'} ·{' '}
          {formatSize(totalSize)} · Read-only
        </p>
      </div>

      <section className={styles.sharedFileCard}>
        <div className={styles.sharedBreadcrumb}>
          <button onClick={() => void navigate({ search: {} })}>{label}</button>
          {breadcrumbs.map((part, index) => {
            const path = breadcrumbs.slice(0, index + 1).join('/');
            return (
              <span key={path} className={styles.sharedCrumbPart}>
                <CaretRight size={12} />
                <button onClick={() => void navigate({ search: { folder: path } })}>{part}</button>
              </span>
            );
          })}
        </div>
        <div className={styles.sharedFileHeader}>
          <span>Name</span>
          <span>Size</span>
          <span>Modified</span>
        </div>
        <div className={styles.sharedFileList}>
          {entries.map((entry) => (
            <button
              key={entry.path}
              className={styles.sharedFileRow}
              onClick={() => openEntry(entry)}
            >
              <span className={styles.sharedFileName}>
                <span className={styles.sharedFileIcon}>
                  <FileGlyph entry={entry} size="row" />
                </span>
                <span>
                  <strong>{entry.name}</strong>
                  <small>{entry.type === 'folder' ? 'Folder' : entry.file.mimeType}</small>
                </span>
              </span>
              <span>{entry.type === 'file' ? formatSize(entry.file.size) : '—'}</span>
              <span>
                {entry.type === 'file' ? <RelativeTime value={entry.file.modifiedAt} /> : '—'}
              </span>
            </button>
          ))}
          {!entries.length && <div className={styles.sharedEmpty}>This folder is empty.</div>}
        </div>
      </section>

      {selected &&
        (isMobile ? (
          <FileDrawer
            title={selected.path.split('/').at(-1) ?? selected.path}
            description="Shared read-only file"
            titleIcon={<FileSheetGlyph file={selected} />}
            onClose={closeFile}
          >
            <SharedFilePreview file={selected} />
          </FileDrawer>
        ) : (
          <Dialog
            size="large"
            open
            onOpenChange={(open) => {
              if (!open) closeFile();
            }}
            title={selected.path.split('/').at(-1) ?? selected.path}
            description="Shared read-only file"
            titleIcon={<FileSheetGlyph file={selected} />}
          >
            <SharedFilePreview file={selected} />
          </Dialog>
        ))}
    </div>
  );
}

function SharedFilePreview({ file }: { file: RepositoryFile }) {
  return (
    <>
      <div className={shared.filePreviewToolbar}>
        <Button size="small" onClick={() => downloadRepositoryFile(file)}>
          <DownloadSimple size={12} /> Download
        </Button>
      </div>
      <FileContentPreview file={file} />
    </>
  );
}
